// SolutionSection.js - Section 3 : Automatisation acquisition client
import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import {
  MdRocketLaunch,
  MdCheckCircle,
  MdPeople,
  MdRepeat,
  MdEventNote,
  MdChat,
  MdMarkEmailRead,
  MdStorage,
  MdAutoAwesome,
  MdTrendingUp,
  MdArrowForward,
  MdOutlineTimer,
  MdOutlineFlashOn,
  MdDns,
  MdInsights,
  MdVerified
} from 'react-icons/md';
import { FaWhatsapp, FaChartLine } from 'react-icons/fa';
import { GiAstronautHelmet, GiStarsStack, GiCrystalBall } from 'react-icons/gi';
import './SolutionSection.css';

const SolutionSection = () => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const isInView = useInView(sectionRef, { once: false, amount: 0.2 });
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.95, 1, 1, 0.95]);

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const progress = Math.min(1, Math.max(0, -rect.top / (rect.height - window.innerHeight)));
        setScrollProgress(progress);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const automationItems = [
    { icon: <MdPeople />, title: "La prospection", color: "#3B82F6", delay: 0 },
    { icon: <MdRepeat />, title: "Les relances", color: "#60A5FA", delay: 0.1 },
    { icon: <MdEventNote />, title: "La prise de rendez-vous", color: "#3B82F6", delay: 0.2 },
    { icon: <MdChat />, title: "Le suivi client", color: "#60A5FA", delay: 0.3 },
    { icon: <FaWhatsapp />, title: "Messages WhatsApp", color: "#25D366", delay: 0.4 },
    { icon: <MdMarkEmailRead />, title: "Email & SMS", color: "#3B82F6", delay: 0.5 },
    { icon: <MdAutoAwesome />, title: "Communication régulière", color: "#8B5CF6", delay: 0.6 },
    { icon: <MdStorage />, title: "Gestion CRM", color: "#10B981", delay: 0.7 }
  ];

  const benefits = [
    { value: "70%", label: "gain de temps", icon: <MdOutlineTimer />, description: "sur les tâches répétitives" },
    { value: "3x", label: "plus de prospects", icon: <MdTrendingUp />, description: "qualifiés par mois" },
    { value: "24/7", label: "automatisation", icon: <MdOutlineFlashOn />, description: "qui travaille pour vous" },
    { value: "100%", label: "fidélisation", icon: <MdVerified />, description: "client améliorée" }
  ];

  return (
    <section className="sol3-root" ref={sectionRef}>
      {/* Background orbs animés */}
      <div className="sol3-orbs">
        <div className="sol3-orb sol3-orb-s1" style={{ transform: `translate(${scrollProgress * 80}px, -${scrollProgress * 40}px)` }} />
        <div className="sol3-orb sol3-orb-s2" style={{ transform: `translate(-${scrollProgress * 60}px, ${scrollProgress * 50}px)` }} />
        <div className="sol3-orb sol3-orb-s3" style={{ transform: `translate(${scrollProgress * 50}px, ${scrollProgress * 30}px)` }} />
      </div>

      {/* Éléments flottants */}
      <div className="sol3-floating">
        <GiAstronautHelmet className="sol3-float-icon sol3-f1" />
        <GiStarsStack className="sol3-float-icon sol3-f2" />
        <GiCrystalBall className="sol3-float-icon sol3-f3" />
      </div>

      <motion.div 
        className="sol3-container"
        style={{ opacity, scale }}
      >
        {/* Header */}
        <motion.div 
          className="sol3-header"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <motion.div 
            className="sol3-badge-wrapper"
            whileHover={{ scale: 1.03 }}
          >
            <span className="sol3-badge">
              <MdRocketLaunch className="sol3-badge-icon" />
              <span>Solution 03 — Automatisation</span>
              <GiStarsStack className="sol3-badge-icon" />
            </span>
            <div className="sol3-badge-glow"></div>
          </motion.div>

          <motion.h2 
            className="sol3-title"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <span className="sol3-title-line">
              Nous <span className="sol3-title-gradient">automatisons</span> votre
            </span>
            <span className="sol3-title-line">
              acquisition client et
              <span className="sol3-title-highlight"> plusieurs process</span>
            </span>
            <div className="sol3-title-underline"></div>
          </motion.h2>

          <motion.p 
            className="sol3-subtitle"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Chez ETEF Design, nous créons des systèmes simples pour vous aider à attirer, suivre, 
            <br />relancer et convertir vos prospects <span className="sol3-highlight">automatiquement</span>.
          </motion.p>
        </motion.div>

        {/* Grille des automatisations */}
        <motion.div 
          className="sol3-automation-grid"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <div className="sol3-automation-header">
            <h3>
              <FaChartLine className="sol3-header-icon" />
              Ce que <span className="sol3-gradient-text">nous automatisons</span>
            </h3>
            <div className="sol3-automation-header-line"></div>
          </div>

          <div className="sol3-automation-items-grid">
            {automationItems.map((item, index) => (
              <motion.div
                key={index}
                className="sol3-automation-card"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.4 + item.delay }}
                whileHover={{ y: -8, scale: 1.02 }}
              >
                <div className="sol3-automation-card-glow" style={{ background: `radial-gradient(circle, ${item.color}20, transparent 80%)` }} />
                <div className="sol3-automation-icon-wrapper" style={{ borderColor: `${item.color}40`, background: `${item.color}10` }}>
                  <div className="sol3-automation-icon" style={{ color: item.color }}>
                    {item.icon}
                  </div>
                </div>
                <h4 className="sol3-automation-title">{item.title}</h4>
                <div className="sol3-automation-check">
                  <MdCheckCircle style={{ color: item.color }} />
                  <span>Automatisé</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Statistiques/Benefits */}
        <motion.div 
          className="sol3-benefits"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.8 }}
        >
          {benefits.map((benefit, index) => (
            <motion.div 
              key={index}
              className="sol3-benefit-card"
              whileHover={{ y: -5, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
            >
              <div className="sol3-benefit-icon">{benefit.icon}</div>
              <div className="sol3-benefit-value">{benefit.value}</div>
              <div className="sol3-benefit-label">{benefit.label}</div>
              <div className="sol3-benefit-description">{benefit.description}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div 
          className="sol3-cta-wrapper"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 1 }}
        >
          <div className="sol3-cta-card">
            <div className="sol3-cta-glow"></div>
            <div className="sol3-cta-pattern"></div>
            
            <div className="sol3-cta-content">
              <motion.div 
                className="sol3-cta-icon"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <MdAutoAwesome />
              </motion.div>
              <h3>Prêt à <span className="sol3-gradient-text">automatiser</span> votre croissance ?</h3>
              <p>Découvrez comment nos systèmes d'automatisation peuvent transformer votre acquisition client.</p>
              
              <div className="sol3-cta-features">
                <div className="sol3-cta-feature-item">
                  <MdDns />
                  <span>Sans engagement</span>
                </div>
                <div className="sol3-cta-feature-item">
                  <MdInsights />
                  <span>Audit offert</span>
                </div>
                <div className="sol3-cta-feature-item">
                  <MdVerified />
                  <span>Garantie résultat</span>
                </div>
              </div>
            </div>
            
            <motion.button 
              className="sol3-cta-button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>Automatiser mon acquisition</span>
              <MdArrowForward />
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default SolutionSection;